import {
  Button,
  CircularProgress,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { enqueueSnackbar } from "notistack";
import { ReactElement, useContext, useState } from "react";
import * as React from "react";

import useCreateCategory from "../../../hooks/mutation/category/use-create-category";
import useText from "../../../hooks/use-text";
import { CategoryContext } from "../../../providers/category-provider";
import { Flatten, flattenV2 } from "../../../utils/flatten";
import { LocalizedStrings, SupportedLanguages } from "../../../utils/lang";

type Props = {
  isOpen: boolean;
  onClose(): void;
};

export default function CreateCategoryDialog(props: Props): ReactElement {
  const { isOpen, onClose } = props;

  const [name, setName] = useState<string>();
  const [order, setOrder] = useState(0);
  const [parentId, setParentId] = useState<string>();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { strings } = useText(local);
  const { categories, refetch } = useContext(CategoryContext);
  const { createCategory } = useCreateCategory();

  // Note: Initialize category menu items
  const categoryItems: ReactElement[] = [];
  flattenV2(categories).map((item: Flatten) => {
    categoryItems.push(
      <MenuItem className="text-[12px]" key={item.id} value={item.id}>
        {item.name}
      </MenuItem>
    );

    if (item.children) {
      item.children.map((family: Flatten) => {
        categoryItems.push(
          <MenuItem
            className="text-[12px] pl-[30px]"
            key={family.id}
            value={family.id}
          >
            {family.name}
          </MenuItem>
        );
      });
    }
  });

  function onIndexChange(value: string): void {
    if (value < "1") {
      setOrder(1);
    } else {
      setOrder(parseInt(value));
    }
  }

  async function onCreate(): Promise<void> {
    setIsSubmitting(true);
    try {
      await createCategory({
        name: name,
        parentId: parentId,
        orderIndex: order,
      });
      await refetch();
      internalClose();
      enqueueSnackbar(strings.info.success, { variant: "success" });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(strings.info.failed, { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  }

  function internalClose(): void {
    setIsSubmitting(false);
    setName("");
    setParentId(undefined);
    setOrder(0);
    onClose();
  }

  return (
    <Dialog open={isOpen} onClose={internalClose}>
      <DialogTitle>{strings.title}</DialogTitle>
      <DialogContent className="min-w-[300px]">
        <TextField
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setName(e.target.value)
          }
          autoFocus
          focused
          margin="dense"
          id="name"
          type="text"
          fullWidth
          placeholder={strings.form.name.placeholder}
          variant="standard"
          value={name}
        />
        <TextField
          onChange={(e) => onIndexChange(e.target.value)}
          margin="dense"
          id="name"
          label={strings.form.order.label}
          type="number"
          variant="standard"
          value={order}
        />
        <br />
        <FormControl className="min-w-[150px] mt-15" size="small">
          <InputLabel className="text-[12px]">Parent Category</InputLabel>
          <Select
            className="border-0 rounded-[15px] text-[12px] text-left"
            value={parentId ?? ""}
            label={strings.form.parentCategory.label}
            onChange={(e) => setParentId(e.target.value || undefined)}
          >
            <MenuItem className="text-[12px]" key="none" value="">
              {strings.form.parentCategory.none}
            </MenuItem>
            {categoryItems}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={internalClose}>{strings.abort}</Button>
        <Button
          disabled={!name || name.length < 3 || isSubmitting}
          onClick={onCreate}
        >
          {isSubmitting ? (
            <Box className="flex w-full justify-center">
              <CircularProgress size="1rem" className="w-1 h-1" />
            </Box>
          ) : (
            strings.create
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type LocalSchema = {
  title: string;
  form: {
    name: {
      placeholder: string;
    };
    order: {
      label: string;
    };
    parentCategory: {
      label: string;
      none: string;
    };
  };
  info: {
    success: string;
    failed: string;
  };
  create: string;
  abort: string;
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    title: "Create category",
    form: {
      name: {
        placeholder: "Enter category name",
      },
      order: {
        label: "Order index",
      },
      parentCategory: {
        label: "Parent Category",
        none: "None",
      },
    },
    info: {
      success: "Category created successfully",
      failed: "Error occured during the category creation. Please try it again",
    },
    create: "Create",
    abort: "Close",
  },
};
